import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, UtensilsCrossed, ClipboardList, BedDouble, Settings, BarChart3, Plus, Trash2, Save, Upload, Image as ImgIcon, CalendarDays } from "lucide-react";

const PKR = (n) => `Rs ${Number(n || 0).toLocaleString("en-PK")}`;
const SECTIONS = [
  { k: "overview", label: "Overview", icon: <BarChart3 size={16} /> },
  { k: "orders", label: "Orders", icon: <ClipboardList size={16} /> },
  { k: "menu", label: "Menu", icon: <UtensilsCrossed size={16} /> },
  { k: "rooms", label: "Rooms", icon: <BedDouble size={16} /> },
  { k: "bookings", label: "Bookings", icon: <CalendarDays size={16} /> },
  { k: "settings", label: "Settings", icon: <Settings size={16} /> },
];
const ORDER_STATUSES = ["pending", "approved", "preparing", "out_for_delivery", "completed", "cancelled"];

export default function OwnerDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="pt-28 text-center text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/" />;
  if (user.role !== "owner") return <div className="pt-28 text-center"><h1 className="font-serif text-3xl text-white">Owner access only</h1></div>;

  return (
    <div className="pt-20" data-testid="owner-dashboard">
      <div className="dash-shell">
        <aside className="border-r border-zinc-900 bg-[#09090B] p-4">
          <div className="flex items-center gap-2 text-amber-200 mb-5">
            <LayoutDashboard size={18} /> <span className="font-serif text-lg">Owner</span>
          </div>
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
            {SECTIONS.map((s) => (
              <button key={s.k} data-testid={`own-tab-${s.k}`} onClick={() => setTab(s.k)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap ${tab === s.k ? "bg-amber-500/10 text-amber-200 border border-amber-500/30" : "text-zinc-400 hover:text-white"}`}>
                {s.icon} {s.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="p-4 sm:p-6 min-h-[70vh]">
          {tab === "overview" && <Overview />}
          {tab === "orders" && <Orders />}
          {tab === "menu" && <MenuAdmin />}
          {tab === "rooms" && <RoomsAdmin />}
          {tab === "bookings" && <BookingsAdmin />}
          {tab === "settings" && <SettingsAdmin />}
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/owner/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-zinc-500">Loading…</div>;
  const items = [
    { label: "Pending orders", value: s.pending_orders, hint: `of ${s.total_orders} total` },
    { label: "Pending bookings", value: s.pending_bookings, hint: `of ${s.total_bookings} total` },
    { label: "Menu items", value: s.menu_items },
    { label: "Rooms", value: s.rooms },
    { label: "Revenue (approved+)", value: PKR(s.revenue) },
  ];
  return (
    <div>
      <h1 className="font-serif text-3xl text-white mb-6">Overview</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.label} className="card-lux p-5" data-testid={`stat-${it.label}`}>
            <div className="text-xs uppercase font-mono tracking-widest text-zinc-500">{it.label}</div>
            <div className="text-3xl text-amber-200 font-mono mt-2">{it.value}</div>
            {it.hint && <div className="text-xs text-zinc-500 mt-1">{it.hint}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
 const load = useCallback(() => {
  return api.get("/owner/orders", { params: filter ? { status: filter } : {} })
    .then((r) => setList(r.data));
}, [filter]);

useEffect(() => {
  load();
}, [load]);
  const update = async (ord, patch) => {
    await api.put(`/owner/orders/${ord.order_number}`, patch);
    load();
    if (sel && sel.order_number === ord.order_number) setSel({ ...sel, ...patch });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl text-white">Orders</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-lux w-48" data-testid="orders-filter">
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        {list.map((o) => (
          <div key={o.order_number} className="card-lux p-4" data-testid={`ord-${o.order_number}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-amber-200">{o.order_number}</div>
                <div className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()} · {o.customer_name} · {o.customer_phone}</div>
                <div className="text-xs text-zinc-400 mt-1">{o.items.length} items · {PKR(o.total)} · {o.payment_method}</div>
              </div>
              <div className="text-right space-y-2">
                <span className={`status-tag status-${o.status}`}>{o.status.replace(/_/g, " ")}</span>
                <div>
                  <select value={o.status} onChange={(e) => update(o, { status: e.target.value })} className="input-lux !py-1 text-xs">
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button className="btn-outline-gold !py-1.5 text-xs" onClick={() => setSel(o)}>View details</button>
              {o.payment_method !== "cod" && o.payment_status === "pending_verification" && (
                <>
                  <button className="btn-outline-gold !py-1.5 text-xs" onClick={() => update(o, { payment_status: "verified" })}>Mark payment verified</button>
                  <button className="btn-outline-gold !py-1.5 text-xs" onClick={() => update(o, { payment_status: "rejected" })}>Reject payment</button>
                </>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="text-zinc-500">No orders.</div>}
      </div>

      {sel && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSel(null)} />
          <div className="relative card-lux w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
            <h3 className="font-serif text-xl text-amber-200">{sel.order_number}</h3>
            <div className="text-xs text-zinc-500 mb-3">{new Date(sel.created_at).toLocaleString()}</div>
            <div className="text-sm text-zinc-300">{sel.customer_name} — {sel.customer_phone}</div>
            <div className="text-sm text-zinc-400">{sel.address}, {sel.city}</div>
            {sel.notes && <div className="text-sm text-zinc-400 mt-1">Note: {sel.notes}</div>}
            <div className="divider-gold my-3" />
            <ul className="space-y-1 text-sm">
              {sel.items.map((i, idx) => <li key={idx} className="flex justify-between"><span>{i.quantity}× {i.name}{i.variant ? ` (${i.variant})` : ""}</span><span className="font-mono">{PKR(i.unit_price * i.quantity)}</span></li>)}
            </ul>
            <div className="divider-gold my-3" />
            <div className="text-sm">Total: <span className="font-mono text-amber-200">{PKR(sel.total)}</span></div>
            <div className="text-sm mt-1">Payment: {sel.payment_method} ({sel.payment_status}){sel.payment_reference ? ` · ref ${sel.payment_reference}` : ""}</div>
            {sel.payment_receipt_b64 && <img src={sel.payment_receipt_b64} alt="receipt" className="mt-3 w-full rounded-md border border-amber-500/30" />}
            <button onClick={() => setSel(null)} className="btn-outline-gold mt-4 w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "menu");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={value} alt="" className="w-16 h-16 rounded-md object-cover border border-zinc-700" /> : <div className="w-16 h-16 rounded-md border border-dashed border-zinc-700 grid place-items-center text-zinc-600"><ImgIcon size={18} /></div>}
      <label className="btn-outline-gold cursor-pointer !py-2"><Upload size={14} /> <span className="text-xs">{busy ? "Uploading…" : "Upload image"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={up} />
      </label>
      <input className="input-lux flex-1" placeholder="or paste image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function MenuAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const blank = { name: "", description: "", category: "pizza", price: 0, image_url: "", variants: [], is_available: true };
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/menu", { params: { all: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  const save = async () => {
    const payload = { ...editing, price: Number(editing.price), variants: editing.variants || [] };
    if (editing.item_id) await api.put(`/owner/menu/${editing.item_id}`, payload);
    else await api.post("/owner/menu", payload);
    setEditing(null); load();
  };
  const del = async (it) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/owner/menu/${it.item_id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl text-white">Menu items</h1>
        <button data-testid="add-menu-btn" onClick={() => setEditing({ ...blank })} className="btn-gold"><Plus size={16} /> New item</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.item_id} className="card-lux p-4" data-testid={`mitem-${it.item_id}`}>
            <div className="flex gap-3">
              {it.image_url && <img src={it.image_url} alt={it.name} className="w-16 h-16 rounded-md object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium line-clamp-1">{it.name}</div>
                <div className="text-xs text-zinc-500">{it.category} · {PKR(it.price)}</div>
                <div className="text-xs mt-1">{it.is_available ? <span className="text-green-400">Available</span> : <span className="text-red-400">Hidden</span>}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(it)} className="btn-outline-gold !py-1.5 text-xs flex-1">Edit</button>
              <button onClick={() => del(it)} className="btn-outline-gold !py-1.5 text-xs"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <div className="relative card-lux w-full max-w-2xl p-5 max-h-[85vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-amber-200 mb-4">{editing.item_id ? "Edit" : "New"} menu item</h3>
            <div className="grid gap-3">
              <input className="input-lux" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <textarea className="input-lux" rows={2} placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <select className="input-lux" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input type="number" className="input-lux" placeholder="Starter price (PKR)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </div>
              <ImageUploader value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />

              <div>
                <div className="text-xs text-zinc-400 mb-2">Variants (optional)</div>
                {(editing.variants || []).map((v, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input className="input-lux" placeholder="Name (e.g. Large)" value={v.name} onChange={(e) => {
                      const nxt = [...editing.variants]; nxt[idx] = { ...v, name: e.target.value }; setEditing({ ...editing, variants: nxt });
                    }} />
                    <input type="number" className="input-lux w-40" placeholder="Price delta" value={v.price_delta || 0} onChange={(e) => {
                      const nxt = [...editing.variants]; nxt[idx] = { ...v, price_delta: Number(e.target.value) }; setEditing({ ...editing, variants: nxt });
                    }} />
                    <button onClick={() => { const nxt = editing.variants.filter((_, i) => i !== idx); setEditing({ ...editing, variants: nxt }); }} className="btn-outline-gold !px-2"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => setEditing({ ...editing, variants: [...(editing.variants || []), { name: "", price_delta: 0 }] })} className="btn-outline-gold !py-1.5 text-xs"><Plus size={12} /> Add variant</button>
              </div>

              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={editing.is_available} onChange={(e) => setEditing({ ...editing, is_available: e.target.checked })} /> Available
              </label>

              <div className="flex gap-2 justify-end mt-2">
                <button onClick={() => setEditing(null)} className="btn-outline-gold">Cancel</button>
                <button onClick={save} className="btn-gold"><Save size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoomsAdmin() {
  const [rooms, setRooms] = useState([]);
  const blank = { name: "", description: "", image_url: "", capacity: 2, total_units: 1, amenities: [], is_active: true };
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/rooms").then((r) => setRooms(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = { ...editing, amenities: typeof editing.amenities === "string" ? editing.amenities.split(",").map((s) => s.trim()).filter(Boolean) : editing.amenities };
    if (editing.room_id) await api.put(`/owner/rooms/${editing.room_id}`, payload);
    else await api.post("/owner/rooms", payload);
    setEditing(null); load();
  };
  const del = async (r) => { if (!window.confirm("Delete room?")) return; await api.delete(`/owner/rooms/${r.room_id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl text-white">Rooms</h1>
        <button data-testid="add-room-btn" onClick={() => setEditing({ ...blank })} className="btn-gold"><Plus size={16} /> New room</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map((r) => (
          <div key={r.room_id} className="card-lux p-4">
            {r.image_url && <img src={r.image_url} alt={r.name} className="w-full aspect-[5/4] object-cover rounded-md" />}
            <div className="mt-2 text-white">{r.name}</div>
            <div className="text-xs text-zinc-500">{r.capacity} guests · {r.total_units} units</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing({ ...r, amenities: (r.amenities || []).join(", ") })} className="btn-outline-gold !py-1.5 text-xs flex-1">Edit</button>
              <button onClick={() => del(r)} className="btn-outline-gold !py-1.5 text-xs"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <div className="relative card-lux w-full max-w-xl p-5 max-h-[85vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-amber-200 mb-4">{editing.room_id ? "Edit" : "New"} room</h3>
            <div className="grid gap-3">
              <input className="input-lux" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <textarea className="input-lux" rows={2} placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <ImageUploader value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" min={1} className="input-lux" placeholder="Capacity" value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: Number(e.target.value) })} />
                <input type="number" min={1} className="input-lux" placeholder="Total identical units" value={editing.total_units} onChange={(e) => setEditing({ ...editing, total_units: Number(e.target.value) })} />
              </div>
              <input className="input-lux" placeholder="Amenities (comma-separated)" value={editing.amenities} onChange={(e) => setEditing({ ...editing, amenities: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active (visible to customers)
              </label>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditing(null)} className="btn-outline-gold">Cancel</button>
                <button onClick={save} className="btn-gold"><Save size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BookingsAdmin() {
  const [bk, setBk] = useState([]);
  const load = () => api.get("/owner/bookings").then((r) => setBk(r.data));
  useEffect(() => { load(); }, []);
  const update = async (b, status) => { await api.put(`/owner/bookings/${b.booking_number}`, { status }); load(); };
  return (
    <div>
      <h1 className="font-serif text-3xl text-white mb-4">Bookings</h1>
      <div className="space-y-2">
        {bk.map((b) => (
          <div key={b.booking_number} className="card-lux p-4 flex items-start justify-between gap-3" data-testid={`bk-${b.booking_number}`}>
            <div>
              <div className="font-mono text-amber-200">{b.booking_number}</div>
              <div className="text-xs text-zinc-500">{new Date(b.created_at).toLocaleString()}</div>
              <div className="text-sm text-zinc-300 mt-1">{b.room_name} · {b.check_in} → {b.check_out} · {b.guests} guests</div>
              <div className="text-xs text-zinc-400">{b.customer_name} · {b.customer_phone}{b.customer_email ? ` · ${b.customer_email}` : ""}</div>
              {b.notes && <div className="text-xs text-zinc-500 mt-1">{b.notes}</div>}
            </div>
            <div className="text-right space-y-2">
              <span className={`status-tag status-${b.status}`}>{b.status}</span>
              <div className="flex flex-col gap-1">
                {b.status !== "confirmed" && <button onClick={() => update(b, "confirmed")} className="btn-outline-gold !py-1 text-xs">Confirm</button>}
                {b.status !== "cancelled" && <button onClick={() => update(b, "cancelled")} className="btn-outline-gold !py-1 text-xs">Cancel</button>}
                {b.status !== "completed" && <button onClick={() => update(b, "completed")} className="btn-outline-gold !py-1 text-xs">Complete</button>}
              </div>
            </div>
          </div>
        ))}
        {bk.length === 0 && <div className="text-zinc-500">No bookings yet.</div>}
      </div>
    </div>
  );
}

function SettingsAdmin() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-zinc-500">Loading…</div>;

  const saveBiz = async () => { await api.put("/owner/settings", { business: s.business, delivery_fee: Number(s.delivery_fee) }); alert("Saved"); };
  const savePay = async () => { await api.put("/owner/settings", { payment_methods: s.payment_methods }); alert("Saved"); };

  const setBiz = (k, v) => setS({ ...s, business: { ...s.business, [k]: v } });
  const setPm = (key, field, v) => setS({ ...s, payment_methods: { ...s.payment_methods, [key]: { ...s.payment_methods[key], [field]: v } } });

  return (
    <div className="space-y-8 max-w-3xl">
      <section className="card-lux p-5">
        <h2 className="font-serif text-xl text-amber-200 mb-4">Business contact</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="input-lux" placeholder="Name" value={s.business.name || ""} onChange={(e) => setBiz("name", e.target.value)} />
          <input className="input-lux" placeholder="Tagline" value={s.business.tagline || ""} onChange={(e) => setBiz("tagline", e.target.value)} />
          <input className="input-lux" placeholder="Phone" value={s.business.phone || ""} onChange={(e) => setBiz("phone", e.target.value)} />
          <input className="input-lux" placeholder="WhatsApp" value={s.business.whatsapp || ""} onChange={(e) => setBiz("whatsapp", e.target.value)} />
          <input className="input-lux sm:col-span-2" placeholder="Address" value={s.business.address || ""} onChange={(e) => setBiz("address", e.target.value)} />
          <input className="input-lux sm:col-span-2" placeholder="Hours (e.g. 10 AM – 11 PM daily)" value={s.business.hours || ""} onChange={(e) => setBiz("hours", e.target.value)} />
          <input className="input-lux" placeholder="Delivery fee (PKR)" type="number" value={s.delivery_fee} onChange={(e) => setS({ ...s, delivery_fee: e.target.value })} />
        </div>
        <button onClick={saveBiz} className="btn-gold mt-4" data-testid="save-business-btn"><Save size={14} /> Save</button>
      </section>

      <section className="card-lux p-5">
        <h2 className="font-serif text-xl text-amber-200 mb-4">Payment methods</h2>
        {Object.entries(s.payment_methods).map(([key, m]) => (
          <div key={key} className="border border-zinc-800 rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium text-white">{m.label}</div>
              <label className="text-sm flex items-center gap-2 text-zinc-300"><input type="checkbox" checked={m.enabled !== false} onChange={(e) => setPm(key, "enabled", e.target.checked)} /> Enabled</label>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <textarea rows={2} className="input-lux sm:col-span-2" placeholder="Instructions shown at checkout" value={m.instructions || ""} onChange={(e) => setPm(key, "instructions", e.target.value)} />
              {key !== "cod" && <>
                <input className="input-lux" placeholder="Account title" value={m.account_title || ""} onChange={(e) => setPm(key, "account_title", e.target.value)} />
                <input className="input-lux" placeholder="Account number" value={m.account_number || ""} onChange={(e) => setPm(key, "account_number", e.target.value)} />
              </>}
              {key === "bank_transfer" && <>
                <input className="input-lux" placeholder="Bank name" value={m.bank_name || ""} onChange={(e) => setPm(key, "bank_name", e.target.value)} />
                <input className="input-lux" placeholder="IBAN" value={m.iban || ""} onChange={(e) => setPm(key, "iban", e.target.value)} />
              </>}
            </div>
          </div>
        ))}
        <button onClick={savePay} className="btn-gold" data-testid="save-pay-btn"><Save size={14} /> Save payment methods</button>
      </section>

      <section className="card-lux p-5">
        <h2 className="font-serif text-xl text-amber-200 mb-2">Owner account</h2>
        <p className="text-sm text-zinc-400">Signed in as the verified owner. To change the owner email, update the <span className="font-mono text-amber-200">OWNER_EMAIL</span> variable in the backend <span className="font-mono">.env</span> file, restart the backend, and sign in with the new Google account. All other Google sign-ins remain customer accounts.</p>
      </section>
    </div>
  );
}
